// Sales return / credit note / billing return / stock transfer presets.
// Each one recreates the current hard-coded backend print so it can be edited
// in the builder:
//   salesReturnA4      <- helper/return_invoice_types/return_inv_1.ts  ReturnInvoice_2 (srt_* data)
//   salesCreditNoteA4  <- helper/credit_note_types/CreditNote_1.ts      CreditNote_1    (sct_* data)
//   billingReturnA4    <- the same ReturnInvoice_2 layout, fed billing-return (icn_*) data
//   stockTransferA4    <- helper/stock_transfer/StockTransferPrint.ts    StockTransferPrintHTML
import { text, field, hline, vline, box, col, table, fixedBand, tableBand, a4, preset } from "./helpers";

const W = 194; // A4 portrait content width with 8 mm margins

// joinComma(a, b, ...) of the TS templates: non-blank values joined with ", ".
const joinComma = (...exprs) => `MID(${exprs.map((e) => `IF(${e}, ", " & ${e}, "")`).join(" & ")}, 3)`;

// Bill To / Ship To details printed from the pay-to / ship-to address objects
// (cmr_pay_* / cmr_ship_* keys). Blank when the address object is missing.
function addressLines(obj, k) {
  const f = (name) => `${obj}.cmr_${k}_${name}`;
  const dl = joinComma(f("dl_no_20_20b"), f("dl_no_21_21b"), f("dl_no_21c"), f("dl_no_21f"));
  return (
    `IF(${obj}, COALESCE(${f("address_name")}, "") & "\\n" & ` +
    `${joinComma(f("address_street"), f("address_town"), f("address_city"), f("address_state"), f("address_pincode"))}` +
    ` & IF(${f("fssai_no")}, "\\n**FSSAI NO:** " & ${f("fssai_no")}, "")` +
    ` & IF(${dl}, "\\n**DL NO:** " & ${dl}, "")` +
    ` & IF(${f("gst")}, "\\n**GST NO:** " & ${f("gst")}, "")` +
    ` & IF(${f("pan")}, "\\n**PAN NO:** " & ${f("pan")}, ""), "")`
  );
}

// Item column widths (mm). Column boundaries are reused by the header and
// totals sections so they line up like the single HTML table of the TS print:
//   cols 1-7 end at X_SHIP (Bill To | Ship To), cols 1-11 at X_DOC (From | doc no),
//   cols 1-12 at X_TOT (GST table | totals), col 15 starts at X_VAL (totals values).
const COLW = [5, 16, 14, 26, 13, 14, 17, 8, 11, 11, 9, 13, 9, 14, 14];
const colEnd = (n) => COLW.slice(0, n).reduce((a, b) => a + b, 0);
const X_SHIP = colEnd(7);
const X_DOC = colEnd(11);
const X_TOT = colEnd(12);
const X_VAL = colEnd(14);

/*
 * Shared layout of ReturnInvoice_2 and CreditNote_1 (they are the same table).
 * cfg: docType, name, title, docRows (right-hand header lines), partyName,
 *      billTo / shipTo (extra lines formula), totalFallbacks (Total Amount column),
 *      variables { gross, tax_amt, disc_amt, other_charge, round_off, total_amt },
 *      remarks (formula)
 */
function returnLayout(cfg) {
  const headerH = 67;
  const titleH = 6;
  const fromH = 30; // 6 .. 36, Bill To / Ship To 36 .. 67
  const partyY = titleH + fromH;
  const partyBlock = (label, lines) => `**${label}**\n**{{ ${cfg.partyName} }}**\n{{ ${lines} }}`;

  // GST summary (nested table on the left of the totals rows): header + up to 6 slabs.
  const taxCols = [
    ["GST%", "item_gst"],
    ["Taxable", "taxable"],
    ["SGST", "sgst"],
    ["CGST", "COALESCE(item.gst, item.cgst)"],
    ["IGST", "igst"],
  ];
  const TX = 2; // tax table x
  const TCW = X_TOT / 10; // each tax column (the tax table is width:50% of the left area)
  const TRH = 4.4;
  const TY = 1.2;
  const taxCells = [];
  taxCols.forEach(([h], c) =>
    taxCells.push(text(TX + c * TCW, TY, TCW, TRH, h, { bold: true, valign: "middle", padding: 0.6, borderWidth: 0.5, borders: c === 0 ? "tlrb" : "trb" }))
  );
  for (let r = 1; r <= 6; r++) {
    taxCols.forEach(([, expr], c) =>
      taxCells.push(
        field(TX + c * TCW, TY + r * TRH, TCW, TRH, `ROW(${r}, ${expr}, "tax")`, "dash", { valign: "middle", padding: 0.6, borderWidth: 0.5, borders: c === 0 ? "lrb" : "rb" }, { visibleIf: `ROWCOUNT("tax") >= ${r}` })
      )
    );
  }

  const totals = [
    ["GrossTotal:", "var.gross"],
    ["TaxAmount:", "var.tax_amt"],
    ["Discount:", "var.disc_amt"],
    ["Other Charge:", "var.other_charge"],
    ["Round Off:", "var.round_off"],
    ["TotalAmount:", "var.total_amt"],
  ];
  const TH = 5.4; // totals row height -> 6 rows = 32.4 mm
  const totalsH = TH * 6;
  const yWords = totalsH;
  const yRemarks = yWords + 6;
  const yBlank = yRemarks + 9;
  const ySign = yBlank + 26;
  const tableBottom = ySign + 10;

  const v = cfg.variables;
  return preset(
    cfg.docType,
    cfg.name,
    a4(8),
    [
      fixedBand(
        "Header",
        headerH,
        [
          box(0, 0, W, headerH),
          text(0, 0, W, titleH, cfg.title, { bold: true, fontSize: 9, align: "center", valign: "middle" }),
          hline(0, titleH, W),
          text(
            1.5,
            titleH + 1,
            X_DOC - 3,
            fromH - 2,
            "From,\n**Store Code:**{{ COALESCE(store.store_code, store.store_id, \"-\") }}\n**{{ COALESCE(store.company, \"-\") }}**\n{{ COALESCE(store.company_address, \"-\") }}\n" +
              "**GST NO.:** {{ COALESCE(store.company_gstn, \"-\") }}\n**Pan NO.:** {{ COALESCE(store.pan_number, store.company_pan, \"-\") }}\n" +
              "**FSSAI NO.:** {{ COALESCE(store.fssai_no, \"-\") }}\n" +
              '**D.L. No.:**  {{ COALESCE(store.dl_no_20_20b, "-") & IF(store.dl_no_21_21b, "," & store.dl_no_21_21b, "") & IF(store.dl_no_21c, "," & store.dl_no_21c, "") }}'
          ),
          vline(X_DOC, titleH, fromH),
          text(X_DOC + 1.5, titleH + 1, W - X_DOC - 3, fromH - 2, cfg.docRows),
          hline(0, partyY, W),
          text(1.5, partyY + 1.2, X_SHIP - 3, headerH - partyY - 2, partyBlock("Bill To", cfg.billTo)),
          vline(X_SHIP, partyY, headerH - partyY),
          text(X_SHIP + 1.5, partyY + 1.2, W - X_SHIP - 3, headerH - partyY - 2, partyBlock("Ship To", cfg.shipTo)),
        ],
        { repeat: "top" }
      ),
      tableBand(
        "Items",
        table(
          "items",
          "batches",
          0,
          W,
          [
            col("sno", "#", "SNO()", COLW[0], { align: "center" }),
            col("code", "ProductCode", "item_code", COLW[1], { format: "dash" }),
            col("hsn", "HsnCode", "item_hsn", COLW[2], { format: "dash" }),
            col("name", "ProductName", "item_name", COLW[3], { format: "dash" }),
            col("uom", "UomName", "item_uom", COLW[4], { format: "dash" }),
            col("batch", "BatchNo", "item.item_batch_number", COLW[5]),
            col("exp", "Exp", "item.item_exp_date", COLW[6], { format: "date:YYYY-MM-DD | dash" }),
            col("qty", "Qty", "COALESCE(item.item_batch_quantity, item.item_quantity, 0)", COLW[7], { align: "right" }),
            col("rate", "Rate", "COALESCE(item.item_batch_purchase_rate, item.item_batch_sales_rate, item.item_unit_price, 0)", COLW[8], { align: "right", format: "fixed:2" }),
            col("mrp", "MRP", "COALESCE(item.item_batch_unit_price, item.item_unit_price, 0)", COLW[9], { align: "right", format: "fixed:2" }),
            col("disc", "Disc %", "COALESCE(item.item_batch_discount_percentage, item.item_discount_percentage, 0)", COLW[10], { align: "right", format: "fixed:2" }),
            col("subtotal", "Subtotal", "IF(NUM(col.total) && NUM(col.taxamt), col.total - col.taxamt, 0)", COLW[11], { align: "right", format: "fixed:2" }),
            col("taxpct", "Tax %", "COALESCE(item.item_batch_tax_percentage, item.item_total_tax_percentage, item.item_gst, 0)", COLW[12], { align: "right", format: "fixed:2" }),
            col("taxamt", "TaxAmount", "COALESCE(item.item_batch_tax_amount, item.item_tax_amount, 0)", COLW[13], { align: "right", format: "fixed:2" }),
            col("total", "Total Amount", `COALESCE(${cfg.totalFallbacks.join(", ")}, 0)`, COLW[14], { align: "right", format: "fixed:2" }),
          ],
          {
            style: { fontSize: 7.5, grid: "all", borderWidth: 0.75, borderColor: "#000000", cellPadding: 0.8 },
            headerStyle: { bold: true, fontSize: 6.5 },
          }
        )
      ),
      fixedBand("Totals", tableBottom + 7, [
        box(0, 0, W, tableBottom),
        ...taxCells,
        vline(X_TOT, 0, totalsH),
        vline(X_VAL, 0, totalsH),
        ...totals.flatMap(([label, expr], i) => [
          i ? hline(X_TOT, i * TH, W - X_TOT) : [],
          text(X_TOT + 1, i * TH, X_VAL - X_TOT - 1.5, TH, label, { valign: "middle" }),
          field(X_VAL + 0.5, i * TH, W - X_VAL - 1.3, TH, expr, "fixed:2", { valign: "middle", align: "right" }),
        ]),
        hline(0, yWords, W),
        text(1.5, yWords, W - 3, 6, "Total Amount(in words): {{ var.total_amt | words:after }}", { valign: "middle" }),
        hline(0, yRemarks, W),
        text(1.5, yRemarks + 0.5, W - 3, 8, "Remarks: {{ " + cfg.remarks + " }}"),
        hline(0, yBlank, W),
        hline(0, ySign, W),
        text(W - 101.5, ySign + 0.8, 100, 8.5, "from, {{ COALESCE(store.company, \"-\") }}\nAuthorised Signature", { align: "right" }),
        text(0, tableBottom + 2, W, 5, "Computer Generated Invoice", { align: "center" }),
      ]),
    ],
    {
      variables: [
        { name: "gross", expr: v.gross },
        { name: "tax_amt", expr: v.tax_amt },
        { name: "disc_amt", expr: v.disc_amt },
        { name: "other_charge", expr: v.other_charge },
        { name: "round_off", expr: v.round_off },
        { name: "total_amt", expr: v.total_amt },
      ],
    }
  );
}

// `a || itemTotal` of the TS: use the order value unless it is blank/zero.
const orItems = (orderExpr, sumExpr) => `IF(NUM(${orderExpr}), ${orderExpr}, ${sumExpr})`;

// Item level fallbacks of ReturnInvoice_2 (`itemTotals`), summed over the item/batch rows.
const ITEM_SUMS = {
  net: "SUM(COALESCE(item.item_batch_total_sales_rate, item.item_total_amount, 0))",
  tax: "SUM(COALESCE(item.item_batch_tax_amount, item.item_tax_amount, 0))",
  disc: "SUM(COALESCE(item.item_batch_discount_amount, item.item_discount_amount, 0))",
};
ITEM_SUMS.sub = `(${ITEM_SUMS.net} - ${ITEM_SUMS.tax})`;

const RETURN_TOTAL_COL = ["item.item_batch_total_sales_rate", "item.item_total_amount"];

export function salesReturnA4() {
  return returnLayout({
    docType: "sales_return",
    name: "Sales Return (A4) - current format",
    title: "SALES RETURN INVOICE",
    docRows:
      '**Sales Return No:** {{ COALESCE(order.srt_invoice_number, "-") }}\n\n' +
      "**Sales Return Date:**\n{{ COALESCE(order.srt_document_date, order.update_date, order.created_date) | date:'DD/MM/YYYY' | dash }}\n\n" +
      "**Payment Terms:**",
    partyName: 'COALESCE(order.cmr_name, "-")',
    billTo: addressLines("order.srt_pay_to_address", "pay"),
    shipTo: addressLines("order.srt_ship_to_address", "ship"),
    totalFallbacks: RETURN_TOTAL_COL,
    variables: {
      gross: orItems("order.srt_sub_total", ITEM_SUMS.sub),
      tax_amt: orItems("order.srt_total_gst", ITEM_SUMS.tax),
      disc_amt: orItems("order.srt_total_discount", ITEM_SUMS.disc),
      other_charge: "COALESCE(order.srt_other_charges, 0)",
      round_off: "COALESCE(order.srt_round_off_value, 0)",
      total_amt: orItems("order.srt_total_amount", ITEM_SUMS.net),
    },
    remarks: 'COALESCE(order.remarks, "")',
  });
}

export function salesCreditNoteA4() {
  return returnLayout({
    docType: "sales_credit_note",
    name: "Sales Credit Note (A4) - current format",
    title: "Sales Credit Note",
    docRows:
      '**Credit Note No:** {{ COALESCE(order.sct_custom_invoice_number, "-") }}\n\n' +
      "**Date:** {{ COALESCE(order.sct_document_date, order.update_date, order.created_date) | date:'DD/MM/YYYY' | dash }}",
    partyName: 'COALESCE(order.cmr_name, "-")',
    billTo: addressLines("order.sct_pay_to_address", "pay"),
    shipTo: addressLines("order.sct_ship_to_address", "ship"),
    totalFallbacks: ["item.item_batch_total_sales_rate", "item.item_batch_total_purchase_rate", "item.item_total_amount"],
    variables: {
      gross: 'COALESCE(order.sct_sub_total, "0.00")',
      tax_amt: 'COALESCE(order.sct_total_gst, "0.00")',
      disc_amt: 'COALESCE(order.sct_total_discount, "0.00")',
      other_charge: 'COALESCE(order.sct_other_charges, "0.00")',
      round_off: 'COALESCE(order.sct_round_off_value, "0.00")',
      total_amt: 'COALESCE(order.sct_total_amount, "0.00")',
    },
    remarks: 'COALESCE(order.remarks, "")',
  });
}

// Billing return (credit note of a billing invoice) printed with ReturnInvoice_2.
// srt_* keys are mapped to their icn_* equivalents; the customer is a walk-in
// (first/last name) and there are no pay-to / ship-to address objects.
export function billingReturnA4() {
  const customer = 'COALESCE(TRIM(order.cmr_first_name & " " & order.cmr_last_name), "-")';
  return returnLayout({
    docType: "billing_return",
    name: "Billing Return (Credit Note) (A4) - current format",
    title: "SALES RETURN INVOICE",
    docRows:
      '**Sales Return No:** {{ COALESCE(order.icn_invoice_number, order.invoice_number, "-") }}\n\n' +
      "**Sales Return Date:**\n{{ COALESCE(order.update_date, order.created_date) | date:'DD/MM/YYYY' | dash }}\n\n" +
      "**Payment Terms:**",
    partyName: customer,
    billTo: 'COALESCE(order.cmr_phone_number, "") & IF(order.icn_billing_address, "\\n" & order.icn_billing_address, "")',
    shipTo: '""',
    totalFallbacks: RETURN_TOTAL_COL,
    variables: {
      gross: orItems("order.icn_sub_total", ITEM_SUMS.sub),
      tax_amt: orItems("order.icn_total_gst", ITEM_SUMS.tax),
      disc_amt: orItems("order.icn_total_discount", ITEM_SUMS.disc),
      other_charge: "COALESCE(order.icn_other_charges, 0)",
      round_off: "COALESCE(order.round_off_value, 0)",
      total_amt: orItems("order.icn_total_amount", ITEM_SUMS.net),
    },
    remarks: 'COALESCE(order.icn_remarks, order.remarks, "")',
  });
}

// Stock In / Stock Out print (A4 landscape), one row per batch.
export function stockTransferA4() {
  const LW = 281; // A4 landscape content width with 8 mm margins
  // 10 columns; header cells span 3 | 2 | 3 | 2 of them like the TS table.
  const widths = [22, 70, 28, 25, 16, 20, 28, 26, 22, 24];
  const at = (n) => widths.slice(0, n).reduce((a, b) => a + b, 0);
  const x1 = at(3);
  const x2 = at(5);
  const x3 = at(8);
  const rows = [6, 7, 9, 13]; // title, document, store code, address
  const ys = rows.reduce((acc, h) => [...acc, acc[acc.length - 1] + h], [0]);
  const H = ys[ys.length - 1];
  const cell = (x, y, w, h, content, bold) => text(x + 1.5, y, w - 3, h, content, { bold: !!bold, valign: "middle" });
  const storeCode = (s) => `{{ COALESCE(extra.${s}.store_code, order.${s === "fromStore" ? "from" : "to"}_warehouse_name, "-") & IF(extra.${s}.firm_name, " - " & extra.${s}.firm_name, "") }}`;
  const address = (s) =>
    `{{ COALESCE(${joinComma(...["firm_address", "firm_area", "firm_city", "firm_state", "firm_pin_code"].map((k) => `extra.${s}.${k}`))}, "-") | dash }}`;

  return preset(
    "stock_transfer",
    "Stock Transfer (A4) - current format",
    { ...a4(8), orientation: "landscape" },
    [
      fixedBand(
        "Header",
        H,
        [
          box(0, 0, LW, H),
          text(0, 0, LW, rows[0], '{{ IF(order.stock_management_action == "stock_in", "Stock In", "Stock Out") }}', { bold: true, fontSize: 9, align: "center", valign: "middle" }),
          ...[1, 2, 3].map((i) => hline(0, ys[i], LW)),
          ...[x1, x2, x3].map((x) => vline(x, ys[1], H - ys[1])),
          cell(0, ys[1], x1, rows[1], "Document Number:", true),
          cell(x1, ys[1], x2 - x1, rows[1], '{{ COALESCE(order.stock_transfer_doc_number, "-") }}'),
          cell(x2, ys[1], x3 - x2, rows[1], "Document Date:", true),
          cell(x3, ys[1], LW - x3, rows[1], "{{ COALESCE(order.transfer_date, order.posting_date) | date:'DD-MM-YYYY' | dash }}"),
          cell(0, ys[2], x1, rows[2], "From Store Code", true),
          cell(x1, ys[2], x2 - x1, rows[2], storeCode("fromStore")),
          cell(x2, ys[2], x3 - x2, rows[2], "To Store Code", true),
          cell(x3, ys[2], LW - x3, rows[2], storeCode("toStore")),
          cell(0, ys[3], x1, rows[3], "Address", true),
          cell(x1, ys[3], x2 - x1, rows[3], address("fromStore")),
          cell(x2, ys[3], x3 - x2, rows[3], "Address", true),
          cell(x3, ys[3], LW - x3, rows[3], address("toStore")),
          // DRAFT watermark (body::before in the TS print) - drawn over the page centre.
          {
            ...text(LW / 2 - 80, 194 / 2 - 18, 160, 36, "DRAFT", { fontSize: 80, align: "center", valign: "middle", color: "#000000", opacity: 0.08 }),
            rotate: -30,
            visibleIf: 'LOWER(order.stock_transfer_status) == "draft"',
          },
        ],
        { repeat: "top" }
      ),
      tableBand(
        "Items",
        table(
          "items",
          "batches",
          0,
          LW,
          [
            col("code", "P. code", "item_code", widths[0], { format: "dash" }),
            col("name", "Product Name", "item_name", widths[1], { format: "dash" }),
            col("batch", "Batch no.", "item.item_batch_number", widths[2], { format: "dash" }),
            col("exp", "Expiry Date", "item.item_exp_date", widths[3], { format: "date:DD-MM-YYYY | dash" }),
            col("qty", "Qty", "COALESCE(item.item_batch_quantity, 0)", widths[4], { align: "right" }),
            col("uom", "UOM", "item_uom", widths[5], { format: "dash" }),
            col("purchase", "Purchase Rate", "COALESCE(item.item_batch_purchase_rate, item.item_batch_unit_price, 0)", widths[6], { align: "right", format: "fixed:2" }),
            col("sales", "Sales Rate", "COALESCE(item.item_batch_unit_price, 0)", widths[7], { align: "right", format: "fixed:2" }),
            col("mrp", "MRP", "COALESCE(item.item_batch_mrp, col.sales)", widths[8], { align: "right", format: "fixed:2" }),
            col("total", "Total", "col.qty * col.purchase", widths[9], { align: "right", format: "fixed:2" }),
          ],
          {
            // The TS print only lists batch rows (items without batches are skipped).
            filter: "NOT(ISBLANK(item.item_batch_quantity))",
            style: { fontSize: 8, grid: "all", borderWidth: 0.75, borderColor: "#000000", cellPadding: 1.2 },
            headerStyle: { bold: true },
          }
        )
      ),
      fixedBand("Footer", 8, [text(0, 2.5, LW, 5, "Computer Generated Invoice", { align: "center" })]),
    ]
  );
}
